import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { QuickEnquiryForm } from '../components/QuickEnquiryForm';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, User, Tag, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogDetailPage = () => {
  const { blogId } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  useEffect(() => {
    fetchBlogDetail();
  }, [blogId]);

  const fetchBlogDetail = async () => {
    try {
      const response = await axios.get(`${API}/blog/${blogId}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Blog not found');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact/enquiry`, contactForm);
      toast.success('Thank you! We will contact you soon.');
      setContactForm({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2463] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-4xl font-bold text-[#0A2463] mb-4">Blog Not Found</h1>
          <p className="text-gray-600">The blog you're looking for doesn't exist.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-8 bg-gradient-to-br from-[#0A2463] via-[#0d2d6e] to-[#247BA0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="flex items-center justify-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{new Date(blog.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <User size={16} />
                <span>{blog.author}</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{blog.title}</h1>
            <p className="text-xl text-gray-200">{blog.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
            {/* Blog Content */}
            <div className="lg:col-span-2">
              {/* Featured Image */}
              <img
                src={blog.image_url}
                alt={blog.title}
                className="w-full h-96 object-cover rounded-2xl shadow-xl mb-8"
              />

              {/* Tags */}
              <div className="flex items-center gap-2 mb-8 flex-wrap">
                <Tag size={18} className="text-[#247BA0]" />
                {blog.tags.split(',').map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-sm">
                    {tag.trim()}
                  </Badge>
                ))}
              </div>

              {/* Contact Detail Banner */}
              <Card className="mb-8 bg-gradient-to-r from-[#FFB703] to-[#FB8500] border-none">
                <CardContent className="p-6">
                  <p className="text-white text-lg font-semibold text-center">
                    📞 {blog.contact_detail}
                  </p>
                </CardContent>
              </Card>

              {/* Blog Content */}
              <div className="prose prose-lg max-w-none">
                <div 
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  style={{ wordBreak: 'break-word' }}
                >
                  {blog.description.split('\n').map((paragraph, idx) => {
                    // Handle markdown-style headers
                    if (paragraph.startsWith('# ')) {
                      return <h1 key={idx} className="text-4xl font-bold text-[#0A2463] mt-8 mb-4">{paragraph.replace('# ', '')}</h1>;
                    } else if (paragraph.startsWith('## ')) {
                      return <h2 key={idx} className="text-3xl font-bold text-[#0A2463] mt-6 mb-3">{paragraph.replace('## ', '')}</h2>;
                    } else if (paragraph.startsWith('### ')) {
                      return <h3 key={idx} className="text-2xl font-bold text-[#247BA0] mt-5 mb-3">{paragraph.replace('### ', '')}</h3>;
                    } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      return <p key={idx} className="font-bold text-[#0A2463] my-3">{paragraph.replace(/\*\*/g, '')}</p>;
                    } else if (paragraph.trim() === '') {
                      return <br key={idx} />;
                    } else if (paragraph.startsWith('- ')) {
                      return <li key={idx} className="ml-6 my-2">{paragraph.replace('- ', '')}</li>;
                    } else {
                      return <p key={idx} className="my-3">{paragraph}</p>;
                    }
                  })}
                </div>
              </div>
            </div>

            {/* Sidebar - Contact Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <Card className="border-none shadow-2xl">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-[#0A2463] mb-6 text-center">
                      Contact Us
                    </h3>

                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                          placeholder="Your name"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                          placeholder="your@email.com"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                          placeholder="+91 XXXXX XXXXX"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="service">Service *</Label>
                        <Input
                          id="service"
                          required
                          value={contactForm.service}
                          onChange={(e) => setContactForm({...contactForm, service: e.target.value})}
                          placeholder="Service you need"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          required
                          value={contactForm.message}
                          onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                          placeholder="Your message..."
                          rows={4}
                          className="mt-2"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-[#0A2463] to-[#247BA0] hover:from-[#247BA0] hover:to-[#0A2463] text-white py-6"
                      >
                        Submit Enquiry
                      </Button>
                    </form>

                    {/* Contact Info */}
                    <div className="mt-8 pt-8 border-t border-gray-200 space-y-4">
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <Phone size={18} className="text-[#0A2463]" />
                        <div>
                          <a href="tel:9316085383" className="hover:text-[#0A2463]">9316085383</a>
                          <br />
                          <a href="tel:9755085383" className="hover:text-[#0A2463]">9755085383</a>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <Mail size={18} className="text-[#0A2463]" />
                        <a href="mailto:Neelesh@utkarshudyam.com" className="hover:text-[#0A2463]">
                          Neelesh@utkarshudyam.com
                        </a>
                      </div>
                      
                      <div className="flex items-start gap-3 text-sm text-gray-600">
                        <MapPin size={18} className="text-[#0A2463] mt-1" />
                        <span>Office 6, Shivkrupa Residency, Vatva, Ahmedabad - 382440</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Enquiry Button */}
                <Button
                  onClick={() => setIsEnquiryOpen(true)}
                  className="w-full mt-6 bg-gradient-to-r from-[#FFB703] to-[#FB8500] hover:from-[#FB8500] hover:to-[#FFB703] text-white py-6 text-lg"
                >
                  Quick Enquiry (Multi-Service)
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <QuickEnquiryForm 
        isOpen={isEnquiryOpen} 
        onClose={() => setIsEnquiryOpen(false)}
      />
    </div>
  );
};

export default BlogDetailPage;
